import { useCallback, useRef, useState } from 'react';
import { removeBlocksKeepingEditable } from './utils/selectionOps';
import { blurActiveEditable, clearNativeSelection } from './utils/selectionDom';
import useSelectionHotkeys from './hooks/useSelectionHotkeys';
import useSelectAll from './hooks/useSelectAll';
import useDragBlockSelect from './hooks/useDragBlockSelect';

/**
 * Handle-selected blocks, drag-across multi-selection, and keyboard shortcuts
 * (Delete, Escape, Ctrl/Cmd+C/X, progressive Ctrl/Cmd+A) for whole-block ops.
 *
 * @param {object} [options]
 * @param {(ids: string[]) => boolean | Promise<boolean>} [options.beforeDeleteBlocks]
 *   Return false / rejected promise to abort Delete/Backspace/Cut.
 */
export const useEditorSelection = (blocksRef, commit, editorRootRef, options = {}) => {
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [multiSelected, setMultiSelected] = useState([]);
  const multiSelectedRef = useRef(multiSelected);
  multiSelectedRef.current = multiSelected;
  const selectedBlockIdRef = useRef(selectedBlockId);
  selectedBlockIdRef.current = selectedBlockId;
  const beforeDeleteRef = useRef(options.beforeDeleteBlocks);
  beforeDeleteRef.current = options.beforeDeleteBlocks;
  // Anchor for shift+click range selection (and drag-across) — stays on the
  // first click / drag-start block until a plain click sets a new one.
  const rangeAnchorRef = useRef(null);

  const gateDelete = useCallback((ids, proceed) => {
    const gate = beforeDeleteRef.current;
    if (!gate) {
      proceed();
      return;
    }
    Promise.resolve(gate(ids))
      .then((ok) => {
        if (ok !== false) proceed();
      })
      .catch(() => {});
  }, []);

  const selectBlockOnly = useCallback((blockId) => {
    if (!blockId) return;
    clearNativeSelection();
    blurActiveEditable();
    setMultiSelected((prev) => (prev.length ? [] : prev));
    setSelectedBlockId(blockId);
    rangeAnchorRef.current = blockId;
  }, []);

  const selectAllTopLevel = useCallback(() => {
    const topIds = (blocksRef.current || []).map((b) => b.id);
    if (!topIds.length) return;
    clearNativeSelection();
    blurActiveEditable();
    setSelectedBlockId(null);
    setMultiSelected(topIds);
    rangeAnchorRef.current = topIds[0];
  }, [blocksRef]);

  const deleteSelected = useCallback(() => {
    if (!selectedBlockId) return;
    const id = selectedBlockId;
    gateDelete([id], () => {
      commit(removeBlocksKeepingEditable(blocksRef.current, [id]));
      setSelectedBlockId(null);
    });
  }, [blocksRef, commit, selectedBlockId, gateDelete]);

  const deleteMultiSelected = useCallback(() => {
    if (!multiSelectedRef.current.length) return;
    const ids = [...multiSelectedRef.current];
    gateDelete(ids, () => {
      commit(removeBlocksKeepingEditable(blocksRef.current, ids));
      setMultiSelected([]);
    });
  }, [blocksRef, commit, gateDelete]);

  useSelectionHotkeys({
    blocksRef,
    commit,
    selectedBlockId,
    multiSelected,
    multiSelectedRef,
    deleteSelected,
    deleteMultiSelected,
    setSelectedBlockId,
    setMultiSelected,
  });

  useSelectAll({
    blocksRef,
    editorRootRef,
    selectedBlockIdRef,
    multiSelectedRef,
    selectBlockOnly,
    selectAllTopLevel,
  });

  useDragBlockSelect({
    blocksRef,
    editorRootRef,
    rangeAnchorRef,
    multiSelectedRef,
    setSelectedBlockId,
    setMultiSelected,
  });

  return {
    selectedBlockId,
    setSelectedBlockId,
    multiSelected,
    setMultiSelected,
  };
};
